import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Building,
  Cpu,
  ClipboardList,
  MapPin,
  Scale,
  Handshake,
  Users,
  X,
  Star } from
"lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader, AlertBanner } from "../../../components/ui";
import { customerService, formatAccountStatusLabel, extractBillingAddressFlag, extractAssetQuantity, toBooleanFlag } from "../../../services/customerService";
import { CRM_ONBOARDING_LIST_PATH } from "../routes";

const NO_AMC_TYPE = "No AMC";

function toAmcTypeDisplay(rawValue) {
  return String(rawValue || "").trim().replace(/_/g, " ");
}

function isNoAmcType(asset = {}) {
  const raw = asset.amcType || asset.amc_type || asset.AMCType || asset.amctype || "";
  return toAmcTypeDisplay(raw) === NO_AMC_TYPE;
}

function formatContactTypeLabel(typeRaw) {
  if (!typeRaw) return "Secondary";
  const str = String(typeRaw).trim().toLowerCase();
  if (str === "primary") return "Primary";
  if (str === "secondary") return "Secondary";
  if (str === "technical") return "Technical";
  if (str === "emergency") return "Emergency";
  if (str === "general") return "General";
  return "Secondary";
}

/** Mobile-only compact table; desktop uses DetailRow layout. */
function ViewCustomerTable({ columns, children }) {
  return (
    <div className="md:hidden w-full">
      <table className="w-full table-fixed text-xs">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-wide text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30">
            {columns.map((col) =>
            <th
              key={col.key || col}
              className="px-2 py-2 font-semibold break-words align-top leading-tight"
              style={col.width ? { width: col.width } : undefined}>
              
                {col.label || col}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-text-border)]/35">{children}</tbody>
      </table>
    </div>);

}

function formatStatusBadge(statusRaw) {
  const label = formatAccountStatusLabel(statusRaw);
  const status = String(statusRaw || "").toLowerCase();
  if (status === "active") return { label, className: "border-emerald-200 text-emerald-700 bg-emerald-50" };
  if (status.includes("hold")) return { label, className: "border-amber-200 text-amber-700 bg-amber-50" };
  if (status === "inactive") return { label, className: "border-rose-200 text-rose-700 bg-rose-50" };
  return { label, className: "border-blue-200 text-blue-700 bg-blue-50" };
}

function DetailRow({ label, value, divided = false }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div
      className={`flex flex-col gap-1 ${
      divided ?
      "pb-3 mb-3 border-b border-[var(--color-text-border)]/40 last:pb-0 last:mb-0 last:border-b-0" :
      ""}`
      }>
      
      <span className="text-[10px] font-bold uppercase text-[var(--color-text-secondary)]">{label}</span>
      <span className="text-sm text-foreground break-words">{isEmpty ? "—" : String(value)}</span>
    </div>);

}

function ReadOnlyDetailModal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 animate-in fade-in duration-200">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full border border-white/60 overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
          <h3 className="text-md font-bold text-foreground">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded-lg hover:bg-slate-100 grid place-items-center text-slate-400"
            aria-label={i18n.t("formActions.close")}>
            
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">{children}</div>
        <div className="flex justify-end gap-2 p-5 pt-0 border-t border-slate-100 mt-2 shrink-0">
          <Button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg border text-xs bg-white text-foreground">
            <Trans i18nKey="formActions.close" />
          
          </Button>
        </div>
      </div>
    </div>);

}

function TabEmptyState({ message }) {
  return (
    <p className="text-sm text-[var(--color-text-secondary)] py-6 text-center">{message}</p>);

}

function TabErrorState({ message }) {
  return (
    <p className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{message}</p>);

}

export function CustomerViewPage() {
  const { customerId } = useParams();
  const [activeTab, setActiveTab] = useState("basic-info");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [customer, setCustomer] = useState(null);
  const [taxLegal, setTaxLegal] = useState(null);
  const [taxLegalError, setTaxLegalError] = useState("");
  const [commercialTerms, setCommercialTerms] = useState(null);
  const [commercialTermsError, setCommercialTermsError] = useState("");
  const [relationship, setRelationship] = useState(null);
  const [relationshipError, setRelationshipError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [detailModal, setDetailModal] = useState(null);

  const loadAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setErrorMsg("");
    setTaxLegalError("");
    setCommercialTermsError("");
    setRelationshipError("");

    try {
      const [cust, contactsRes, locationsRes, assetsRes] = await Promise.all([
      customerService.getCustomerById(customerId),
      customerService.getContactsByCustomer(customerId, 0, 100),
      customerService.getLocationsByCustomer(customerId, 0, 100),
      customerService.getAssetsByCustomer(customerId, 0, 100)]
      );

      setCustomer(cust || null);

      const locationList = locationsRes?.locations || [];
      const enrichedLocations = await Promise.all(
        locationList.map(async (loc) => {
          const locationId = loc.id || loc.locationId || loc.location_id;
          if (!locationId) return loc;
          try {
            const detail = await customerService.getLocationById(locationId);
            return { ...loc, ...(detail || {}) };
          } catch {
            return loc;
          }
        })
      );
      setLocations(enrichedLocations);

      const contactList = contactsRes?.contacts || [];
      setContacts(contactList);

      const assetList = assetsRes?.assets || [];
      const enrichedAssets = await Promise.all(
        assetList.map(async (asset) => {
          const assetId = asset.id || asset.assetId || asset.asset_id;
          if (!assetId) return asset;
          try {
            const detail = await customerService.getAssetById(assetId);
            return { ...asset, ...(detail || {}) };
          } catch {
            return asset;
          }
        })
      );
      setAssets(enrichedAssets);

      try {
        const tax = await customerService.getTaxInfoByCustomerId(customerId);
        setTaxLegal(tax || null);
      } catch (err) {
        setTaxLegal(null);
        setTaxLegalError(err?.message || "Failed to load tax & legal details.");
      }

      try {
        const terms = await customerService.getCommercialTermsByCustomerId(customerId);
        setCommercialTerms(terms || null);
      } catch (err) {
        setCommercialTerms(null);
        setCommercialTermsError(err?.message || "Failed to load commercial terms.");
      }

      try {
        const rel = await customerService.getRelationshipByCustomerId(customerId);
        setRelationship(rel || null);
      } catch (err) {
        setRelationship(null);
        setRelationshipError(err?.message || "Failed to load relationship profile.");
      }
    } catch (err) {
      console.error("Failed to load customer view:", err);
      setErrorMsg(err?.message || "Failed to load customer profile.");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openContactDetail = async (contact) => {
    const id = contact.id || contact.contactId;
    try {
      const detail = id ? await customerService.getContactById(id) : contact;
      setDetailModal({ type: "contact", data: detail || contact });
    } catch {
      setDetailModal({ type: "contact", data: contact });
    }
  };

  const openLocationDetail = async (location) => {
    const id = location.id || location.locationId || location.location_id;
    try {
      const detail = id ? await customerService.getLocationById(id) : null;
      setDetailModal({ type: "location", data: { ...location, ...(detail || {}) } });
    } catch {
      setDetailModal({ type: "location", data: location });
    }
  };

  const openAssetDetail = async (asset) => {
    const id = asset.id || asset.assetId || asset.asset_id;
    try {
      const detail = id ? await customerService.getAssetById(id) : null;
      setDetailModal({ type: "asset", data: { ...asset, ...(detail || {}) } });
    } catch {
      setDetailModal({ type: "asset", data: asset });
    }
  };

  const companyName = customer?.companyName || customer?.company_name || "Customer";
  const statusMeta = formatStatusBadge(customer?.accountStatus || customer?.account_status);

  const locationNameById = (locationId) => {
    const loc = locations.find(
      (l) => String(l.id || l.locationId || l.location_id) === String(locationId)
    );
    return loc?.locationName || loc?.location_name || "—";
  };

  const formatAccountType = (typeRaw) => {
    const str = String(typeRaw || "").trim();
    if (!str) return "—";
    return str.replace(/_/g, " ");
  };

  const tabs = [
  { id: "basic-info", label: "Information", icon: Building },
  { id: "tax-legal", label: "Tax & Legal", icon: Scale },
  { id: "commercial-terms", label: "Commercial Terms", icon: ClipboardList },
  { id: "relationship", label: "Relationship", icon: Handshake },
  { id: "contacts", label: `Contacts (${contacts.length})`, icon: Users },
  { id: "locations", label: `Locations (${locations.length})`, icon: MapPin },
  { id: "assets", label: `Assets (${assets.length})`, icon: Cpu }];


  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-3 pb-10">
        <div className="flex flex-col gap-2">
          <PageBackLink to={CRM_ONBOARDING_LIST_PATH} />

          <PageHeader
            className="mb-0"
            title={`View Profile: ${companyName}`}
            />
          
        </div>

        {errorMsg && <AlertBanner type="error">{errorMsg}</AlertBanner>}

        <div className="sticky top-0 z-20 bg-[var(--color-workspace)] pb-2">
          <div className="flex flex-wrap gap-2 border-b border-[var(--color-text-border)]/50 pb-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
                  isActive ?
                  "bg-[var(--color-primary)] text-white shadow-[0_4px_12px_rgba(30,51,131,0.2)]" :
                  "hover:bg-[var(--color-workspace)] text-[var(--color-text-secondary)] hover:text-foreground"}`
                  }>
                  
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>);

            })}
          </div>
        </div>

        <div className="relative z-0 bg-white rounded-2xl border border-white/60 p-6 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] space-y-4">
          {loading ?
          <div className="flex flex-col gap-3 py-4">
              {[1, 2, 3].map((n) =>
            <div key={n} className="h-10 rounded-xl bg-slate-100 animate-pulse" />
            )}
            </div> :

          <>
              {activeTab === "basic-info" &&
            <div className="space-y-5">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.basicInformation" /></h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DetailRow label="Company Name" value={customer?.companyName || customer?.company_name} />
                    <DetailRow label="Industry Sector" value={customer?.industryType || customer?.industry_type} />
                    <DetailRow
                  label="Account Type"
                  value={formatAccountType(customer?.accountType || customer?.account_type)} />
                
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold uppercase text-[var(--color-text-secondary)]"><Trans i18nKey="crm.accountStatus2" /></span>
                      <span className={`inline-flex w-fit px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                  </div>
                </div>
            }

              {activeTab === "tax-legal" &&
            <div className="space-y-4">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.taxLegalDetails" /></h3>
                  {taxLegalError && <TabErrorState message={taxLegalError} />}
                  {!taxLegalError && !taxLegal ?
              <TabEmptyState message="No tax & legal details recorded." /> :
              taxLegal ?
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DetailRow label="Legal Name" value={taxLegal.legalName || taxLegal.legal_name} />
                      <DetailRow label="Entity Type" value={taxLegal.entityType || taxLegal.entity_type} />
                      <DetailRow label="GSTIN" value={taxLegal.gstin} />
                      <DetailRow label="PAN" value={taxLegal.pan} />
                      <DetailRow
                  label="GST Registration Type"
                  value={taxLegal.gstRegistrationType || taxLegal.gst_registration_type} />
                
                      <DetailRow
                  label="TDS Deductor"
                  value={taxLegal.isTdsDeductor ?? taxLegal.is_tds_deductor ? "Yes" : "No"} />
                
                    </div> :
              null}
                </div>
            }

              {activeTab === "commercial-terms" &&
            <div className="space-y-4">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.commercialPaymentTerms" /></h3>
                  {commercialTermsError && <TabErrorState message={commercialTermsError} />}
                  {!commercialTermsError && !commercialTerms ?
              <TabEmptyState message="No commercial terms recorded." /> :
              commercialTerms ?
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DetailRow label="Payment Terms" value={commercialTerms.paymentTerms || commercialTerms.payment_terms} />
                      <DetailRow
                  label="Custom Credit Days"
                  value={
                  commercialTerms.paymentTermsCustomDays ??
                  commercialTerms.payment_terms_custom_days ??
                  commercialTerms.customDays ??
                  commercialTerms.custom_days
                  } />
                
                      <DetailRow
                  label="Credit Limit"
                  value={commercialTerms.creditLimit ?? commercialTerms.credit_limit} />
                
                      <DetailRow
                  label="Retention Percentage (%)"
                  value={commercialTerms.retentionPercentage ?? commercialTerms.retention_percentage} />
                
                      <DetailRow
                  label="Retention Release"
                  value={commercialTerms.retentionRelease || commercialTerms.retention_release} />
                
                      <DetailRow
                  label="Security Deposit Amount"
                  value={commercialTerms.securityDepositAmount ?? commercialTerms.security_deposit_amount} />
                
                      <DetailRow label="Billing Cycle" value={commercialTerms.billingCycle || commercialTerms.billing_cycle} />
                      <DetailRow
                  label="Preferred Payment Mode"
                  value={commercialTerms.preferredPaymentMode || commercialTerms.preferred_payment_mode} />
                
                      <DetailRow label="Rate Card Tier" value={commercialTerms.rateCardTier || commercialTerms.rate_card_tier} />
                    </div> :
              null}
                </div>
            }

              {activeTab === "relationship" &&
            <div className="space-y-4">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.relationshipProfile" /></h3>
                  {relationshipError && <TabErrorState message={relationshipError} />}
                  {!relationshipError && !relationship ?
              <TabEmptyState message="No relationship profile recorded." /> :
              relationship ?
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DetailRow label="Lead Source" value={relationship.leadSource || relationship.lead_source} />
                      <DetailRow
                  label="Credit Risk Rating"
                  value={relationship.creditRiskRating || relationship.credit_risk_rating} />
                
                      <DetailRow
                  label="Credit Hold"
                  value={relationship.creditHold ?? relationship.credit_hold ? "Yes" : "No"} />
                
                      <DetailRow
                  label="Preferred Contact Channel"
                  value={relationship.preferredContactChannel || relationship.preferred_contact_channel} />
                
                      <DetailRow
                  label="Account Owner ID"
                  value={relationship.accountOwnerId ?? relationship.account_owner_id} />
                
                    </div> :
              null}
                </div>
            }

              {activeTab === "contacts" &&
            <div className="space-y-5">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.contacts" /></h3>
                  {contacts.length === 0 ?
              <TabEmptyState message="No contacts found." /> :
              <>
                  <div className="hidden md:block space-y-6">
                    {contacts.map((c, idx) => {
                  const id = c.id || c.contactId;
                  const first = c.firstName || c.first_name || "";
                  const last = c.lastName || c.last_name || "";
                  const isPrimary = c.isPrimary ?? c.is_primary;
                  const receivesNotifications = c.receivesNotifications ?? c.receives_notifications;
                  return (
                    <div
                      key={id || `${first}-${c.email}-${idx}`}
                      className={idx > 0 ? "pt-6 border-t border-[var(--color-text-border)]/40" : ""}>
                      
                            {contacts.length > 1 &&
                      <div className="flex items-center gap-2 mb-4">
                                <span className="text-xs font-bold text-[var(--color-primary)] uppercase">{i18n.t("common.contact")}{idx + 1}</span>
                                {isPrimary &&
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700">
                                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                    <Trans i18nKey="crm.primaryContact" />
                                  </span>
                        }
                              </div>
                      }
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <DetailRow label={i18n.t("crm.firstName")} value={first} />
                              <DetailRow label={i18n.t("crm.lastName")} value={last} />
                              <DetailRow label={i18n.t("common.email")} value={c.email} />
                              <DetailRow label={i18n.t("common.phone")} value={c.phone} />
                              <DetailRow label={i18n.t("crm.jobTitle")} value={c.jobTitle || c.job_title} />
                              <DetailRow label={i18n.t("crm.contactType")} value={formatContactTypeLabel(c.contactType || c.contact_type)} />
                              <DetailRow label={i18n.t("crm.setAsPrimaryContact")} value={isPrimary ? i18n.t("common.yes") : i18n.t("common.no")} />
                              <DetailRow label={i18n.t("crm.receiveNotifications")} value={receivesNotifications ? i18n.t("common.yes") : i18n.t("common.no")} />
                            </div>
                          </div>);

                })}
                  </div>
                  <ViewCustomerTable
                columns={[
                { label: "Name", key: "name", width: "38%" },
                { label: "Phone", key: "phone", width: "28%" },
                { label: "Email", key: "email", width: "34%" }]
                }>
                
                      {contacts.map((c) => {
                  const id = c.id || c.contactId;
                  const first = c.firstName || c.first_name || "";
                  const last = c.lastName || c.last_name || "";
                  return (
                    <tr
                      key={id || `${first}-${c.email}`}
                      className="hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => openContactDetail(c)}>
                      
                            <td className="px-2 py-2 font-semibold text-foreground break-words align-top leading-snug">{`${first} ${last}`.trim() || "—"}</td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{c.phone || "—"}</td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{c.email || "—"}</td>
                          </tr>);

                })}
                    </ViewCustomerTable>
                </>
              }
                </div>
            }

              {activeTab === "locations" &&
            <div className="space-y-5">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.locations" /></h3>
                  {locations.length === 0 ?
              <TabEmptyState message="No locations found." /> :
              <>
                  <div className="hidden md:block space-y-6">
                    {locations.map((l, idx) => {
                  const id = l.id || l.locationId || l.location_id;
                  const isActive = toBooleanFlag(l.isActive ?? l.is_active, true);
                  const isBilling = extractBillingAddressFlag(l, false);
                  return (
                    <div
                      key={id || l.locationName || idx}
                      className={idx > 0 ? "pt-6 border-t border-[var(--color-text-border)]/40" : ""}>
                      
                            {locations.length > 1 &&
                      <p className="text-xs font-bold text-[var(--color-primary)] uppercase mb-4">
                                {i18n.t("common.location")}{idx + 1}
                                {id ? `${i18n.t("crm.idPrefix")}${id}` : ""}
                              </p>
                      }
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <DetailRow label={i18n.t("crm.locationName")} value={l.locationName || l.location_name} />
                              <DetailRow label={i18n.t("crm.siteType")} value={l.siteType || l.site_type} />
                              <DetailRow label={i18n.t("common.addressLine1")} value={l.addressLine1 || l.address_line1} />
                              <DetailRow label={i18n.t("common.addressLine2")} value={l.addressLine2 || l.address_line2} />
                              <DetailRow label={i18n.t("crm.city")} value={l.city} />
                              <DetailRow label={i18n.t("crm.state")} value={l.state} />
                              <DetailRow label={i18n.t("status.active")} value={isActive ? i18n.t("common.yes") : i18n.t("common.no")} />
                              <DetailRow label={i18n.t("crm.billingAddress")} value={isBilling ? i18n.t("common.yes") : i18n.t("common.no")} />
                            </div>
                          </div>);

                })}
                  </div>
                  <ViewCustomerTable
                columns={[
                { label: "Location", key: "name", width: "40%" },
                { label: "City", key: "city", width: "28%" },
                { label: "Site Type", key: "siteType", width: "32%" }]
                }>
                
                      {locations.map((l) => {
                  const id = l.id || l.locationId || l.location_id;
                  return (
                    <tr
                      key={id || l.locationName}
                      className="hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => openLocationDetail(l)}>
                      
                            <td className="px-2 py-2 font-semibold text-foreground break-words align-top leading-snug">
                              {l.locationName || l.location_name || "—"}
                            </td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{l.city || "—"}</td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{l.siteType || l.site_type || "—"}</td>
                          </tr>);

                })}
                    </ViewCustomerTable>
                </>
              }
                </div>
            }

              {activeTab === "assets" &&
            <div className="space-y-5">
                  <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.assets" /></h3>
                  {assets.length === 0 ?
              <TabEmptyState message="No assets found." /> :
              <>
                  <div className="hidden md:block space-y-6">
                    {assets.map((a, idx) => {
                  const id = a.id || a.assetId || a.asset_id;
                  const locId = a.locationId || a.location_id;
                  const warrantyExpiry =
                    a.warrantyExpiry ||
                    a.warranty_expiry ||
                    a.warrantyEndDate ||
                    a.warranty_end_date;
                  const amcStartDate =
                    a.AMCStartDate ||
                    a.amcStartDate ||
                    a.amc_start_date ||
                    a.AMC_Start_Date ||
                    a.amcstartDate;
                  const amcEndDate =
                    a.AMCEndDate ||
                    a.amcEndDate ||
                    a.amc_end_date ||
                    a.AMC_End_Date ||
                    a.amcendDate;
                  const amcType = toAmcTypeDisplay(a.amcType || a.amc_type || a.AMCType || "");
                  return (
                    <div
                      key={id || a.assetName || idx}
                      className={idx > 0 ? "pt-6 border-t border-[var(--color-text-border)]/40" : ""}>
                      
                            {assets.length > 1 &&
                      <p className="text-xs font-bold text-[var(--color-primary)] uppercase mb-4">
                                {i18n.t("crm.asset")}{idx + 1}
                                {id ? `${i18n.t("crm.idPrefix")}${id}` : ""}
                              </p>
                      }
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <DetailRow label={i18n.t("common.location2")} value={locationNameById(locId)} />
                              <DetailRow label={i18n.t("crm.assetName")} value={a.assetName || a.asset_name} />
                              <DetailRow label={i18n.t("common.category2")} value={a.assetCategory || a.asset_category} />
                              <DetailRow label={i18n.t("crm.manufacturer")} value={a.manufacturer} />
                              <DetailRow label={i18n.t("crm.modelNumber")} value={a.modelNumber || a.model_number} />
                              <DetailRow label={i18n.t("crm.serialNumber")} value={a.serialNumber || a.serial_number} />
                              <DetailRow label={i18n.t("dateTime.installDate")} value={a.installDate || a.install_date} />
                              <DetailRow label={i18n.t("crm.warrantyType")} value={a.warrantyType || a.warranty_type} />
                              <DetailRow label={i18n.t("dateTime.warrantyStartDate")} value={a.warrantyStartDate || a.warranty_start_date} />
                              <DetailRow label={i18n.t("dateTime.warrantyEndDate")} value={warrantyExpiry} />
                              <DetailRow label={i18n.t("crm.amcType")} value={amcType} />
                              {!isNoAmcType(a) &&
                        <>
                                  <DetailRow
                            label={i18n.t("crm.amcOrder")}
                            value={a.amcOrder || a.amc_order || a.AMCOrder || a.amcOrderNo || a.amc_order_no} />
                                
                                  <DetailRow
                            label={i18n.t("crm.amcOrderDate")}
                            value={a.amcOrderDate || a.amc_order_date || a.AMCOrderDate} />
                                
                                  <DetailRow label={i18n.t("dateTime.amcStartDate")} value={amcStartDate} />
                                  <DetailRow label={i18n.t("dateTime.amcEndDate")} value={amcEndDate} />
                                </>
                        }
                              <DetailRow label={i18n.t("crm.tenantAssetId")} value={a.tenantAssetId || a.tenant_asset_id || a.TenantAssetId} />
                              <DetailRow label={i18n.t("crm.assetStatus")} value={a.assetStatus || a.asset_status} />
                            </div>
                          </div>);

                })}
                  </div>
                  <ViewCustomerTable
                columns={[
                { label: "Location", key: "location", width: "34%" },
                { label: "Asset", key: "name", width: "40%" },
                { label: "Status", key: "status", width: "26%" }]
                }>
                
                      {assets.map((a) => {
                  const id = a.id || a.assetId || a.asset_id;
                  const locId = a.locationId || a.location_id;
                  const status = a.assetStatus || a.asset_status || "—";
                  return (
                    <tr
                      key={id || a.assetName}
                      className="hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => openAssetDetail(a)}>
                      
                            <td className="px-2 py-2 font-semibold text-foreground break-words align-top leading-snug">{locationNameById(locId)}</td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{a.assetName || a.asset_name || "—"}</td>
                            <td className="px-2 py-2 text-foreground break-words align-top leading-snug">{String(status)}</td>
                          </tr>);

                })}
                    </ViewCustomerTable>
                </>
              }
                </div>
            }
            </>
          }
        </div>
      </div>

      {detailModal?.type === "contact" &&
      <ReadOnlyDetailModal title={i18n.t("crm.contactDetails")} onClose={() => setDetailModal(null)}>
          <div className="grid grid-cols-1">
            <DetailRow
            divided
            label="Contact Name"
            value={`${detailModal.data.firstName || detailModal.data.first_name || ""} ${detailModal.data.lastName || detailModal.data.last_name || ""}`.trim()} />
          
            <DetailRow divided label="Contact Type" value={detailModal.data.contactType || detailModal.data.contact_type} />
            <DetailRow divided label="Email" value={detailModal.data.email} />
            <DetailRow divided label="Phone" value={detailModal.data.phone} />
            <DetailRow divided label="Job Title" value={detailModal.data.jobTitle || detailModal.data.job_title} />
            <DetailRow
            divided
            label="Primary Contact"
            value={detailModal.data.isPrimary ?? detailModal.data.is_primary ? "Yes" : "No"} />
          
            <DetailRow
            divided
            label="Receives Notifications"
            value={detailModal.data.receivesNotifications ?? detailModal.data.receives_notifications ? "Yes" : "No"} />
          
          </div>
        </ReadOnlyDetailModal>
      }

      {detailModal?.type === "location" &&
      <ReadOnlyDetailModal title={i18n.t("crm.locationDetails")} onClose={() => setDetailModal(null)}>
          <div className="grid grid-cols-1">
            <DetailRow divided label="Location Name" value={detailModal.data.locationName || detailModal.data.location_name} />
            <DetailRow divided label="Site Type" value={detailModal.data.siteType || detailModal.data.site_type} />
            <DetailRow divided label="Address Line 1" value={detailModal.data.addressLine1 || detailModal.data.address_line1} />
            <DetailRow divided label="Address Line 2" value={detailModal.data.addressLine2 || detailModal.data.address_line2} />
            <DetailRow divided label="City" value={detailModal.data.city} />
            <DetailRow divided label="State" value={detailModal.data.state} />
            <DetailRow
            divided
            label="Active"
            value={toBooleanFlag(detailModal.data.isActive ?? detailModal.data.is_active, true) ? "Yes" : "No"} />
          
            <DetailRow
            divided
            label="Billing Address"
            value={extractBillingAddressFlag(detailModal.data, false) ? "Yes" : "No"} />
          
          </div>
        </ReadOnlyDetailModal>
      }

      {detailModal?.type === "asset" &&
      <ReadOnlyDetailModal title={i18n.t("crm.assetDetails")} onClose={() => setDetailModal(null)}>
          <div className="grid grid-cols-1">
            <DetailRow
            divided
            label="Location"
            value={locationNameById(detailModal.data.locationId || detailModal.data.location_id)} />
          
            <DetailRow divided label="Asset Name" value={detailModal.data.assetName || detailModal.data.asset_name} />
            <DetailRow divided label="Category" value={detailModal.data.assetCategory || detailModal.data.asset_category} />
            <DetailRow divided label="Manufacturer" value={detailModal.data.manufacturer} />
            <DetailRow divided label="Model Number" value={detailModal.data.modelNumber || detailModal.data.model_number} />
            <DetailRow divided label="Serial Number" value={detailModal.data.serialNumber || detailModal.data.serial_number} />
            <DetailRow divided label="Install Date" value={detailModal.data.installDate || detailModal.data.install_date} />
            <DetailRow divided label="Warranty Type" value={detailModal.data.warrantyType || detailModal.data.warranty_type} />
            <DetailRow
            divided
            label="Warranty Start Date"
            value={detailModal.data.warrantyStartDate || detailModal.data.warranty_start_date} />
          
            <DetailRow
            divided
            label="Warranty Expiry"
            value={
            detailModal.data.warrantyExpiry ||
            detailModal.data.warranty_expiry ||
            detailModal.data.warrantyEndDate ||
            detailModal.data.warranty_end_date
            } />
          
            <DetailRow
            divided
            label="AMC Start Date"
            value={
            detailModal.data.AMCStartDate ||
            detailModal.data.amcStartDate ||
            detailModal.data.amc_start_date ||
            detailModal.data.AMC_Start_Date ||
            detailModal.data.amcstartDate
            } />
          
            <DetailRow
            divided
            label="AMC End Date"
            value={
            detailModal.data.AMCEndDate ||
            detailModal.data.amcEndDate ||
            detailModal.data.amc_end_date ||
            detailModal.data.AMC_End_Date ||
            detailModal.data.amcendDate
            } />
          
            <DetailRow divided label="Status" value={detailModal.data.assetStatus || detailModal.data.asset_status} />
            <DetailRow
            divided
            label="Quantity"
            value={extractAssetQuantity(detailModal.data)} />
          
          </div>
        </ReadOnlyDetailModal>
      }
    </AppLayout>);

}
